import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  TextField,
  Divider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';
import { useCalendar } from '../contexts/CalendarContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getCalendarDays,
  getMonthYearLabel,
  getDayLabels,
  isInCurrentMonth,
  isCurrentDay,
  isPastDate,
  getNextMonth,
  getPreviousMonth,
  formatDate,
  isWeekend,
  isWeekday
} from '../utils/calendarUtils';

export default function Calendar({ includeWeekdays = false, fullHeight = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const {
    unavailableDates,
    loading,
    error,
    markDateUnavailable,
    markDateAvailable,
    updateUnavailableDateReason
  } = useCalendar();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [reason, setReason] = useState('');
  const [bulkReason, setBulkReason] = useState('');

  // Helper function to safely get date from Firestore Timestamp or Date object
  const getDateFromData = (dateData) => {
    if (!dateData) return null;
    if (dateData?.toDate && typeof dateData.toDate === 'function') {
      return dateData.toDate();
    }
    if (dateData instanceof Date) {
      return dateData;
    }
    // If it's a string or other format, try to convert to Date
    try {
      return new Date(dateData);
    } catch (error) {
      console.error('Error converting date data:', dateData, error);
      return null;
    }
  };

  // Get calendar days for current month
  const calendarDays = useMemo(() => {
    return getCalendarDays(currentMonth);
  }, [currentMonth]);

  // Filter unavailable dates to only show current user's dates
  const filteredUnavailableDates = useMemo(() => {
    if (!currentUser) {
      console.log('Calendar: No current user, returning empty array');
      return [];
    }

    // Filter to only include dates belonging to the current user
    const userDates = unavailableDates.filter(dateData => {
      if (!dateData.userId) {
        console.warn('Calendar: Found date without userId:', dateData);
        return false;
      }
      if (dateData.userId !== currentUser.uid) {
        console.warn('Calendar: Found date for different user:', dateData.userId, 'current user:', currentUser.uid);
        return false;
      }
      return true;
    });

    console.log('Calendar: Filtered unavailable dates for user:', currentUser.uid, 'count:', userDates.length);

    if (includeWeekdays) {
      return userDates; // Include all dates when toggle is ON
    } else {
      // Only include weekend dates when toggle is OFF
      return userDates.filter(dateData => {
        const date = getDateFromData(dateData.date);
        return date && isWeekend(date);
      });
    }
  }, [unavailableDates, includeWeekdays, currentUser]);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(getPreviousMonth(currentMonth));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(getNextMonth(currentMonth));
  };

  // Handle date selection (toggle selection)
  const handleDateClick = (date) => {
    if (isPastDate(date)) return; // Don't allow marking past dates

    // When toggle is OFF, only allow weekend dates
    if (!includeWeekdays && isWeekday(date)) return;

    const dateString = date.toDateString();
    const newSelectedDates = new Set(selectedDates);

    if (newSelectedDates.has(dateString)) {
      newSelectedDates.delete(dateString);
    } else {
      newSelectedDates.add(dateString);
    }

    setSelectedDates(newSelectedDates);
  };

  // Handle single date edit (for existing unavailable dates)
  const handleDateEdit = (date) => {
    const existingDate = filteredUnavailableDates.find(d =>
      getDateFromData(d.date).toDateString() === date.toDateString()
    );

    if (existingDate) {
      setEditingDate(existingDate);
      setReason(existingDate.reason || '');
      setShowDatePicker(true);
    }
  };

  // Handle marking multiple dates as unavailable
  const handleBulkMarkUnavailable = async () => {
    if (selectedDates.size === 0) return;

    try {
      const datesArray = Array.from(selectedDates).map(dateString => new Date(dateString));

      for (const date of datesArray) {
        // When toggle is OFF, only allow weekend dates
        if (!includeWeekdays && isWeekday(date)) continue;
        await markDateUnavailable(date, bulkReason);
      }

      // Clear selection after successful operation
      setSelectedDates(new Set());
      setBulkReason('');
    } catch (error) {
      console.error('Error marking dates unavailable:', error);
    }
  };

  // Handle marking multiple dates as available
  const handleBulkMarkAvailable = async () => {
    if (selectedDates.size === 0) return;

    try {
      const datesArray = Array.from(selectedDates);

      for (const dateString of datesArray) {
        const existingDate = filteredUnavailableDates.find(d =>
          getDateFromData(d.date).toDateString() === dateString
        );

        if (existingDate) {
          await markDateAvailable(existingDate.id);
        }
      }

      // Clear selection after successful operation
      setSelectedDates(new Set());
      setBulkReason('');
    } catch (error) {
      console.error('Error marking dates available:', error);
    }
  };

  // Clear all selected dates
  const clearSelection = () => {
    setSelectedDates(new Set());
    setBulkReason('');
  };

  // Check if a date is unavailable
  const isDateUnavailable = (date) => {
    if (!date) return false;
    return filteredUnavailableDates.some(d => {
      const dateFromData = getDateFromData(d.date);
      return dateFromData && dateFromData.toDateString() === date.toDateString();
    });
  };

  // Get unavailable date data for a specific date
  const getUnavailableDateData = (date) => {
    if (!date) return null;
    return filteredUnavailableDates.find(d => {
      const dateFromData = getDateFromData(d.date);
      return dateFromData && dateFromData.toDateString() === date.toDateString();
    });
  };



  // Render calendar day
  // Check if a date is selected
  const isDateSelected = (date) => {
    return selectedDates.has(date.toDateString());
  };

  const renderCalendarDay = (date) => {
    const isCurrentMonth = isInCurrentMonth(date, currentMonth);
    const isToday = isCurrentDay(date);
    const isPast = isPastDate(date);
    const isUnavailable = isDateUnavailable(date);
    const isSelected = isDateSelected(date);
    const unavailableData = getUnavailableDateData(date);
    const isWeekendDay = isWeekend(date);
    const isWeekdayDay = isWeekday(date);

    return (
      <Box
        key={date.toISOString()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: (isPast || (!includeWeekdays && isWeekdayDay)) ? 'default' : 'pointer',
          position: 'relative',
          backgroundColor: isSelected
            ? 'primary.100'
            : isToday
              ? 'transparent'
              : isPast
                ? 'action.hover'
                : (!includeWeekdays && isWeekdayDay)
                  ? 'action.hover'
                  : isUnavailable
                    ? 'error.50'
                    : 'transparent',
          border: isSelected
            ? '2px solid'
            : isToday
              ? '2px solid'
              : '1px solid',
          borderColor: isSelected
            ? 'primary.main'
            : isToday
              ? 'secondary.main'
              : 'divider', // Unified border color for all other states (past, disabled, unavailable)
          borderRadius: 1,
          p: 0.5,
          outline: 'none', // Prevent default focus ring
          overflow: 'hidden', // Clip stripes to border radius
          opacity: isCurrentMonth ? 1 : 0.4,
          '&:hover': !isPast && (includeWeekdays || isWeekendDay) && {
            backgroundColor: isSelected
              ? 'primary.200'
              : isUnavailable
                ? 'error.100'
                : 'action.hover',
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={() => handleDateClick(date)}
        onDoubleClick={() => !isPast && handleDateEdit(date)}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: isToday ? 'bold' : 'normal',
            // mb: 1, // Removed to fix centering
            color: isSelected
              ? 'primary.contrastText'
              : isToday
                ? 'secondary.main'
                : isPast
                  ? 'text.disabled'
                  : (!includeWeekdays && isWeekdayDay)
                    ? 'text.disabled'
                    : isUnavailable
                      ? 'error.main'
                      : 'text.primary',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }}
        >
          {date.getDate()}
        </Typography>

        {/* Personal unavailability indicator - Side Stripe (Left) */}
        {isUnavailable && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              bottom: 4,
              left: 0,
              width: 4,
              borderTopRightRadius: 2,
              borderBottomRightRadius: 2,
              backgroundColor: 'error.main'
            }}
          />
        )}

        {/* Removed "Weekday indicator" dot as per user request */}

        {/* Personal unavailability reason tooltip */}
        {isUnavailable && unavailableData?.reason && (
          <Tooltip title={unavailableData.reason} arrow>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading calendar...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Error: {error}</Typography>
      </Paper>
    );
  }

  // Debug information
  console.log('Calendar: Rendering with currentUser:', currentUser?.uid);
  console.log('Calendar: Total unavailableDates from context:', unavailableDates.length);
  console.log('Calendar: Filtered unavailableDates for current user:', filteredUnavailableDates.length);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', lg: 'row' },
      gap: 3,
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Main Calendar */}
      <Box sx={{
        flex: { xs: '1 1 100%', lg: '0 0 60%' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        <Paper sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Calendar Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={goToPreviousMonth} size="small">
                <ChevronLeftIcon />
              </IconButton>

              <Typography variant="h6" sx={{ minWidth: 120, textAlign: 'center', fontWeight: 'bold' }}>
                {getMonthYearLabel(currentMonth)}
              </Typography>

              <IconButton onClick={goToNextMonth} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Legend - Compact */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 4, borderRadius: 1, bgcolor: 'error.main' }} />
                <Typography variant="caption">Unavailable</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'primary.main' }} />
                <Typography variant="caption">Today</Typography>
              </Box>
            </Box>
          </Box>

          {/* Day Labels */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 0.5 }}>
            {getDayLabels().map((day) => (
              <Box
                key={day}
                sx={{
                  textAlign: 'center',
                  py: 0.5,
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                {isMobile ? day.charAt(0) : day}
              </Box>
            ))}
          </Box>

          {/* Calendar Grid - Flexible Height but constrained */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 1,
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            aspectRatio: '1.1', // Taller boxes (was 1.4)
            mx: 'auto', // Center horizontally only
            alignContent: 'start' // Align to top to reduce gap
          }}>
            {calendarDays.map((date) => (
              <Box key={date.toISOString()} sx={{ height: '100%' }}>
                {renderCalendarDay(date)}
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Side Panel for Selected Dates and Upcoming Dates */}
      <Box sx={{
        flex: { xs: '1 1 100%', lg: '0 0 40%' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflowY: 'auto'
      }}>
        {/* Selected Dates Panel */}
        <Paper sx={{ p: 2, flexShrink: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Selected Dates {selectedDates.size > 0 && `(${selectedDates.size})`}
          </Typography>

          {selectedDates.size === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Click dates to select
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {Array.from(selectedDates).map((dateString) => (
                  <Chip
                    key={dateString}
                    label={new Date(dateString).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
                    size="small"
                    onDelete={() => {
                      const newSelectedDates = new Set(selectedDates);
                      newSelectedDates.delete(dateString);
                      setSelectedDates(newSelectedDates);
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearSelection}
                  fullWidth
                >
                  Clear
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <TextField
                fullWidth
                size="small"
                label="Reason (optional)"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                sx={{ mb: 1 }}
              />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleBulkMarkUnavailable}
                  disabled={selectedDates.size === 0}
                  fullWidth
                  size="small"
                >
                  Mark Unavailable
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleBulkMarkAvailable}
                  disabled={selectedDates.size === 0}
                  fullWidth
                  size="small"
                >
                  Mark Available
                </Button>
              </Box>
            </>
          )}
        </Paper>

        {/* Upcoming Unavailable Dates Panel */}
        <Paper sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventBusyIcon color="error" fontSize="small" />
            Upcoming Unavailable
          </Typography>

          {(() => {
            const today = new Date();
            const upcomingDates = filteredUnavailableDates
              .filter(d => getDateFromData(d.date) >= today)
              .sort((a, b) => getDateFromData(a.date) - getDateFromData(b.date));

            return upcomingDates.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {upcomingDates.map((date) => (
                  <Box key={date.id} sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatDate(getDateFromData(date.date), 'MMM dd')}
                      </Typography>
                      {date.reason && (
                        <Typography variant="caption" color="text.secondary">
                          {date.reason}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={getDateFromData(date.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No upcoming unavailable dates
              </Typography>
            );
          })()}
        </Paper>
      </Box>

      {/* Date Picker Modal for Single Date Edit */}
      {showDatePicker && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowDatePicker(false)}
        >
          <Paper
            sx={{ p: 3, maxWidth: 400, width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" gutterBottom>
              {editingDate?.id ? 'Edit Unavailable Date' : 'Add Unavailable Date'}
            </Typography>

            {editingDate?.id ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getDateFromData(editingDate.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select a date:
                </Typography>
                <input
                  type="date"
                  value={editingDate?.date ? editingDate.date.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    // Create a temporary editing date for the new date
                    setEditingDate({
                      id: null,
                      date: newDate,
                      reason: ''
                    });
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Reason (optional):
              </Typography>
              <input
                type="text"
                value={editingDate?.id ? reason : (editingDate?.reason || '')}
                onChange={(e) => {
                  const newReason = e.target.value;
                  setReason(newReason);
                  if (!editingDate?.id) {
                    // Update the temporary editing date reason
                    setEditingDate(prev => prev ? { ...prev, reason: newReason } : null);
                  }
                }}
                placeholder="e.g., Work meeting, Vacation, etc."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setShowDatePicker(false)}
              >
                Cancel
              </Button>

              {editingDate?.id && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    const date = getDateFromData(editingDate.date);
                    if (!includeWeekdays && isWeekday(date)) return; // Only mark weekdays if toggle is OFF
                    markDateAvailable(editingDate.id);
                    setShowDatePicker(false);
                    setEditingDate(null);
                    setReason('');
                  }}
                >
                  Mark Available
                </Button>
              )}

              <Button
                variant="contained"
                onClick={() => {
                  const date = getDateFromData(editingDate.date);
                  if (!includeWeekdays && isWeekday(date)) return; // Only mark weekdays if toggle is OFF
                  if (editingDate.id) {
                    updateUnavailableDateReason(editingDate.id, reason);
                  } else {
                    markDateUnavailable(date, reason);
                  }
                  setShowDatePicker(false);
                  setEditingDate(null);
                  setReason('');
                }}
                disabled={!editingDate}
              >
                {editingDate?.id ? 'Update' : 'Add Date'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
